import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function LeaveManagement({ user }) {
  const [leaves, setLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    leave_type: 'vacation',
    is_half_day: false,
    half_day_period: 'morning'
  });

  const getLeaveTypeLabel = (type) => {
    const labels = {
      vacation: 'Vacation',
      sick: 'Sick',
      emergency: 'Emergency',
      unpaid: 'Unpaid'
    };
    return labels[type] || type;
  };

  useEffect(() => {
    fetchLeaves();
    fetchLeaveBalance();
  }, []);

  const fetchLeaveBalance = async () => {
    try {
      const response = await authFetch(`${API}/leave/balance`);
      if (response.ok) {
        const balance = await response.json();
        setLeaveBalance(balance);
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };

  const fetchLeaves = async () => {
    try {
      const myLeavesRes = await authFetch(`${API}/leave/my-requests`);
      const myLeaves = await myLeavesRes.json();
      setLeaves(myLeaves);

      if (user.role === 'admin') {
        const allLeavesRes = await authFetch(`${API}/leave/all`);
        const allLeavesData = await allLeavesRes.json();
        setAllLeaves(allLeavesData);
      }
    } catch (error) {
      console.error('Error fetching leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API}/leave/request`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Leave request submitted!');
        setDialogOpen(false);
        setFormData({ start_date: '', end_date: '', reason: '', leave_type: 'vacation', is_half_day: false, half_day_period: 'morning' });
        fetchLeaves();
        fetchLeaveBalance();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit leave request');
      }
    } catch (error) {
      toast.error('Failed to submit leave request');
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/approve`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave approved');
        fetchLeaves();
      } else {
        toast.error('Failed to approve leave');
      }
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/reject`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave rejected');
        fetchLeaves();
      } else {
        toast.error('Failed to reject leave');
      }
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-accent/10 text-accent';
      case 'rejected':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  return (
    <Layout user={user}>
      <div data-testid="leave-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT">Leave Management</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="request-leave-btn" className="bg-primary hover:bg-primary-hover rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Request Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading font-bold">Request Leave</DialogTitle>
              </DialogHeader>

              {leaveBalance && (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-sm font-medium text-foreground-muted mb-2">Available Leave Credits ({new Date().getFullYear()})</p>
                  <div className="flex gap-4">
                    <div>
                      <p className="text-xs text-foreground-muted">Sick Leave</p>
                      <p className="text-2xl font-bold text-foreground-DEFAULT">{leaveBalance.sick_leave}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-muted">Vacation Leave</p>
                      <p className="text-2xl font-bold text-foreground-DEFAULT">{leaveBalance.vacation_leave}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-muted">Emergency Leave</p>
                      <p className="text-2xl font-bold text-foreground-DEFAULT">{leaveBalance.emergency_leave}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      setFormData({
                        ...formData,
                        start_date: newStartDate,
                        // If half day is selected, sync end date with start date
                        end_date: formData.is_half_day ? newStartDate : formData.end_date
                      });
                    }}
                    required
                    className="rounded-lg"
                    data-testid="leave-start-date"
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    disabled={formData.is_half_day}
                    className="rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="leave-end-date"
                  />
                  {formData.is_half_day && (
                    <p className="text-xs text-foreground-muted mt-1">
                      End date is automatically set to match start date for half day leave
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="is_half_day"
                    type="checkbox"
                    checked={formData.is_half_day}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData({
                        ...formData,
                        is_half_day: isChecked,
                        // Auto-set end date to match start date for half day
                        end_date: isChecked ? formData.start_date : formData.end_date
                      });
                    }}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    data-testid="half-day-checkbox"
                  />
                  <Label htmlFor="is_half_day" className="text-sm font-medium cursor-pointer">
                    Half Day Leave (0.5 day)
                  </Label>
                </div>
                {formData.is_half_day && (
                  <div className="space-y-2 -mt-2">
                    <p className="text-xs text-foreground-muted">
                      ℹ️ Half day leave will deduct 0.5 days from your balance
                    </p>
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Select Period</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="half_day_period"
                            value="morning"
                            checked={formData.half_day_period === 'morning'}
                            onChange={(e) => setFormData({ ...formData, half_day_period: e.target.value })}
                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                            data-testid="morning-radio"
                          />
                          <span className="text-sm text-foreground-DEFAULT">Morning</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="half_day_period"
                            value="afternoon"
                            checked={formData.half_day_period === 'afternoon'}
                            onChange={(e) => setFormData({ ...formData, half_day_period: e.target.value })}
                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                            data-testid="afternoon-radio"
                          />
                          <span className="text-sm text-foreground-DEFAULT">Afternoon</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="leave_type">Leave Type</Label>
                  <Select
                    value={formData.leave_type}
                    onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                  >
                    <SelectTrigger className="rounded-lg" data-testid="leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vacation">Vacation Leave</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="emergency">Emergency Leave</SelectItem>
                      <SelectItem value="unpaid">Unpaid Leave (No balance deduction)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.leave_type === 'unpaid' && (
                    <p className="text-xs text-foreground-muted mt-2">
                      ℹ️ Unpaid leave will not deduct from your leave balance
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="reason">Reason</Label>
                  <Input
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="Brief reason for leave"
                    data-testid="leave-reason"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary-hover rounded-full" data-testid="submit-leave-btn">
                  Submit Request
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {leaveBalance && (
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-slate-100 shadow-sm rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-heading font-semibold text-foreground-DEFAULT mb-4">
              Leave Balance for {leaveBalance.year}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-foreground-muted mb-1">Sick Leave</p>
                <p className={`text-3xl font-bold ${leaveBalance.sick_leave < 3 ? 'text-red-600' : 'text-foreground-DEFAULT'}`}>
                  {leaveBalance.sick_leave}
                </p>
                <p className="text-xs text-foreground-muted mt-1">days available</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-foreground-muted mb-1">Vacation Leave</p>
                <p className={`text-3xl font-bold ${leaveBalance.vacation_leave < 3 ? 'text-red-600' : 'text-foreground-DEFAULT'}`}>
                  {leaveBalance.vacation_leave}
                </p>
                <p className="text-xs text-foreground-muted mt-1">days available</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100">
                <p className="text-sm text-foreground-muted mb-1">Emergency Leave</p>
                <p className={`text-3xl font-bold ${leaveBalance.emergency_leave < 1 ? 'text-red-600' : 'text-foreground-DEFAULT'}`}>
                  {leaveBalance.emergency_leave}
                </p>
                <p className="text-xs text-foreground-muted mt-1">days available</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
            <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-4">My Leave Requests</h2>
            {leaves.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                <p className="text-foreground-muted">No leave requests yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaves.map((leave) => (
                  <div
                    key={leave.leave_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-xl gap-4"
                    data-testid={`leave-${leave.leave_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-foreground-DEFAULT">{getLeaveTypeLabel(leave.leave_type)} Leave</span>
                        {leave.is_half_day && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            Half Day ({leave.half_day_period || 'Morning'})
                          </span>
                        )}
                        {leave.leave_type === 'unpaid' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            No balance deduction
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(leave.status)}`}>
                          {leave.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground-muted mb-1">
                        {leave.start_date} to {leave.end_date} • {leave.leave_days || '?'} day(s)
                      </p>
                      <p className="text-sm text-foreground-DEFAULT mb-1">{leave.reason}</p>
                      {leave.created_at && (
                        <p className="text-xs text-foreground-muted">
                          Submitted: {new Date(leave.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {user.role === 'admin' && allLeaves.length > 0 && (
            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
              <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-4">All Leave Requests</h2>
              <div className="space-y-3">
                {allLeaves.map((leave) => (
                  <div
                    key={leave.leave_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-xl gap-4"
                    data-testid={`admin-leave-${leave.leave_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-foreground-DEFAULT">{leave.user_name || 'Unknown Employee'}</span>
                        <span className="font-medium text-foreground-muted">• {getLeaveTypeLabel(leave.leave_type)}</span>
                        {leave.is_half_day && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            Half Day ({leave.half_day_period || 'Morning'})
                          </span>
                        )}
                        {leave.leave_type === 'unpaid' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            Unpaid
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(leave.status)}`}>
                          {leave.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground-muted mb-1">
                        {leave.start_date} to {leave.end_date} • {leave.leave_days || '?'} day(s)
                      </p>
                      <p className="text-sm text-foreground-DEFAULT mb-1">{leave.reason}</p>
                      {leave.created_at && (
                        <p className="text-xs text-foreground-muted">
                          Submitted: {new Date(leave.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                    {leave.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(leave.leave_id)}
                          size="sm"
                          className="bg-accent hover:bg-accent/90 rounded-full"
                          data-testid={`approve-leave-${leave.leave_id}`}
                        >
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(leave.leave_id)}
                          size="sm"
                          variant="destructive"
                          className="rounded-full"
                          data-testid={`reject-leave-${leave.leave_id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default LeaveManagement;